import React, { useState, useMemo } from 'react';
import { Calculator, Home, PiggyBank, ShieldCheck, Calendar, Percent } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';

const MortgageCalculator = () => {
  const [values, setValues] = useState({
    homePrice: 350000,
    downPayment: 70000,
    interestRate: 6.5,
    loanTerm: 30,
    propertyTax: 2400,
    homeInsurance: 1200
  });

  // Calculate down payment percent and PMI using useMemo
  const { downPaymentPercent, pmi } = useMemo(() => {
    const dpPercent = (values.downPayment / values.homePrice) * 100;
    let annualPMI = 0;
    if (dpPercent < 20) {
      const loanAmount = values.homePrice - values.downPayment;
      annualPMI = loanAmount * 0.007; // 0.7% average
    }
    return { downPaymentPercent: dpPercent, pmi: annualPMI };
  }, [values.homePrice, values.downPayment]);

  // Calculate monthly payment using useMemo
  const monthlyPayment = useMemo(() => {
    const loanAmount = values.homePrice - values.downPayment;
    const monthlyInterestRate = values.interestRate / 100 / 12;
    const numberOfPayments = values.loanTerm * 12;

    // Calculate principal & interest using amortization formula
    let monthlyPI = 0;
    if (monthlyInterestRate > 0) {
      monthlyPI = loanAmount * 
        (monthlyInterestRate * Math.pow(1 + monthlyInterestRate, numberOfPayments)) / 
        (Math.pow(1 + monthlyInterestRate, numberOfPayments) - 1);
    } else {
      monthlyPI = loanAmount / numberOfPayments;
    }

    // Calculate interest portion (first month)
    const interestPortion = loanAmount * monthlyInterestRate;
    const principalPortion = monthlyPI - interestPortion;

    const monthlyTaxes = values.propertyTax / 12;
    const monthlyInsurance = values.homeInsurance / 12;
    const monthlyPMI = pmi / 12;

    return {
      principal: principalPortion,
      interest: interestPortion,
      taxes: monthlyTaxes,
      insurance: monthlyInsurance,
      pmi: monthlyPMI,
      total: monthlyPI + monthlyTaxes + monthlyInsurance + monthlyPMI
    };
  }, [values, pmi]);

  const handleHomePriceChange = (value) => {
    const price = parseFloat(value) || 0;
    const downPayment = price * (downPaymentPercent / 100);
    setValues(prev => ({ ...prev, homePrice: price, downPayment }));
  };

  const handleDownPaymentChange = (value) => {
    const dp = parseFloat(value) || 0;
    setValues(prev => ({ ...prev, downPayment: dp }));
  };

  const handleDownPaymentPercentChange = (percent) => {
    const downPayment = values.homePrice * (percent / 100);
    setValues(prev => ({ ...prev, downPaymentPercent: percent, downPayment }));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatCurrencyDetailed = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Calculate pie chart values
  const total = monthlyPayment.total || 1;
  const piSlice = ((monthlyPayment.principal + monthlyPayment.interest) / total) * 100;
  const taxSlice = (monthlyPayment.taxes / total) * 100;
  const insuranceSlice = (monthlyPayment.insurance / total) * 100;
  const pmiSlice = (monthlyPayment.pmi / total) * 100;

  return (
    <section className="py-16 bg-white" data-testid="mortgage-calculator-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#BCA480]/10 rounded-full mb-4">
            <Calculator className="h-8 w-8 text-[#BCA480]" />
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#434345] mb-3" data-testid="calculator-heading">
            Mortgage Calculator
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Estimate your monthly mortgage payment including principal, interest, taxes, and insurance.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Input Section */}
          <div className="bg-gray-50 rounded-xl p-6 lg:p-8 space-y-6">
            {/* Home Price */}
            <div>
              <Label htmlFor="homePrice" className="flex items-center gap-2 mb-2 text-sm font-semibold text-[#434345]">
                <Home className="h-4 w-4 text-[#BCA480]" />
                Home Price
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                <Input
                  id="homePrice"
                  type="number"
                  value={values.homePrice}
                  onChange={(e) => handleHomePriceChange(e.target.value)}
                  className="pl-7 text-lg font-semibold"
                  data-testid="home-price-input"
                />
              </div>
              <Slider
                value={[values.homePrice]}
                onValueChange={(v) => handleHomePriceChange(v[0])}
                max={2000000}
                min={50000}
                step={5000}
                className="mt-3"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>$50k</span>
                <span>$2M</span>
              </div>
            </div>

            {/* Down Payment */}
            <div>
              <Label className="flex items-center gap-2 mb-2 text-sm font-semibold text-[#434345]">
                <PiggyBank className="h-4 w-4 text-[#BCA480]" />
                Down Payment
              </Label>
              <div className="grid grid-cols-2 gap-3">
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <Input
                    type="number"
                    value={Math.round(values.downPayment)}
                    onChange={(e) => handleDownPaymentChange(e.target.value)}
                    className="pl-7"
                    data-testid="down-payment-input"
                  />
                </div>
                <div className="relative">
                  <Input
                    type="number"
                    value={downPaymentPercent.toFixed(1)}
                    onChange={(e) => handleDownPaymentPercentChange(parseFloat(e.target.value) || 0)}
                    className="pr-7"
                    data-testid="down-payment-percent-input"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
                </div>
              </div>
              <Slider
                value={[downPaymentPercent]}
                onValueChange={(v) => handleDownPaymentPercentChange(v[0])}
                max={50}
                min={0}
                step={0.5}
                className="mt-3"
              />
              {downPaymentPercent < 20 && (
                <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                  <ShieldCheck className="h-3 w-3" />
                  PMI required (down payment under 20%)
                </p>
              )}
            </div>

            {/* Interest Rate */}
            <div>
              <Label htmlFor="interestRate" className="flex items-center gap-2 mb-2 text-sm font-semibold text-[#434345]">
                Interest Rate
              </Label>
              <div className="relative">
                <Input
                  id="interestRate"
                  type="number"
                  step="0.125"
                  value={values.interestRate}
                  onChange={(e) => setValues(prev => ({ ...prev, interestRate: parseFloat(e.target.value) || 0 }))}
                  className="pr-7"
                  data-testid="interest-rate-input"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
              </div>
              <Slider
                value={[values.interestRate]}
                onValueChange={(v) => setValues(prev => ({ ...prev, interestRate: v[0] }))}
                max={12}
                min={2}
                step={0.125}
                className="mt-3"
              />
            </div>

            {/* Loan Term */}
            <div>
              <Label className="flex items-center gap-2 mb-2 text-sm font-semibold text-[#434345]">
                <Calendar className="h-4 w-4 text-[#BCA480]" />
                Loan Term
              </Label>
              <div className="flex gap-2">
                {[15, 20, 30].map((term) => (
                  <button
                    key={term}
                    onClick={() => setValues(prev => ({ ...prev, loanTerm: term }))}
                    className={`flex-1 py-2 px-4 rounded-lg font-semibold transition-all ${
                      values.loanTerm === term
                        ? 'bg-[#BCA480] text-white'
                        : 'bg-white border border-gray-300 text-gray-700 hover:border-[#BCA480]'
                    }`}
                    data-testid={`loan-term-${term}`}
                  >
                    {term} years
                  </button>
                ))}
              </div>
            </div>

            {/* Property Tax & Insurance */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="propertyTax" className="text-sm font-semibold text-[#434345] mb-2 block">
                  Annual Property Tax
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <Input
                    id="propertyTax"
                    type="number"
                    value={values.propertyTax}
                    onChange={(e) => setValues(prev => ({ ...prev, propertyTax: parseFloat(e.target.value) || 0 }))}
                    className="pl-7"
                    data-testid="property-tax-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="homeInsurance" className="text-sm font-semibold text-[#434345] mb-2 block">
                  Annual Insurance
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <Input
                    id="homeInsurance"
                    type="number"
                    value={values.homeInsurance}
                    onChange={(e) => setValues(prev => ({ ...prev, homeInsurance: parseFloat(e.target.value) || 0 }))}
                    className="pl-7"
                    data-testid="home-insurance-input"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Results Section */}
          <div className="bg-gradient-to-br from-[#434345] to-[#5a5a5c] rounded-xl p-6 lg:p-8 text-white">
            <div className="text-center mb-8">
              <p className="text-white/70 text-sm uppercase tracking-wide mb-2">Estimated Monthly Payment</p>
              <p className="text-5xl font-bold" data-testid="total-monthly-payment">
                {formatCurrency(monthlyPayment.total)}
              </p>
              <p className="text-white/60 text-sm mt-2">per month</p>
            </div>

            {/* Payment Breakdown */}
            <div className="space-y-4">
              <h4 className="font-semibold text-white/90 border-b border-white/20 pb-2">Payment Breakdown</h4>
              
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#BCA480]"></div>
                  <span className="text-white/80">Principal & Interest</span>
                </div>
                <span className="font-semibold" data-testid="pi-payment">
                  {formatCurrencyDetailed(monthlyPayment.principal + monthlyPayment.interest)}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-blue-400"></div>
                  <span className="text-white/80">Property Taxes</span>
                </div>
                <span className="font-semibold" data-testid="tax-payment">
                  {formatCurrencyDetailed(monthlyPayment.taxes)}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-400"></div>
                  <span className="text-white/80">Home Insurance</span>
                </div>
                <span className="font-semibold" data-testid="insurance-payment">
                  {formatCurrencyDetailed(monthlyPayment.insurance)}
                </span>
              </div>

              {monthlyPayment.pmi > 0 && (
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-amber-400"></div>
                    <span className="text-white/80">PMI</span>
                  </div>
                  <span className="font-semibold" data-testid="pmi-payment">
                    {formatCurrencyDetailed(monthlyPayment.pmi)}
                  </span>
                </div>
              )}
            </div>

            {/* Loan Summary */}
            <div className="mt-8 pt-6 border-t border-white/20 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-white/70">Loan Amount</span>
                <span className="font-semibold">{formatCurrency(values.homePrice - values.downPayment)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-white/70">Total Interest Paid</span>
                <span className="font-semibold">
                  {formatCurrency(
                    (monthlyPayment.principal + monthlyPayment.interest) * values.loanTerm * 12 - 
                    (values.homePrice - values.downPayment)
                  )}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-white/70">Total of All Payments</span>
                <span className="font-semibold">
                  {formatCurrency(monthlyPayment.total * values.loanTerm * 12)}
                </span>
              </div>
            </div>

            {/* CTA */}
            <div className="mt-8 p-4 bg-white/10 rounded-lg text-center">
              <p className="text-sm text-white/80 mb-2">Need help with financing?</p>
              <p className="text-white font-semibold">We can connect you with trusted lenders</p>
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <p className="text-xs text-gray-500 text-center mt-6">
          *This calculator provides estimates for informational purposes only. Actual rates and payments may vary. 
          Contact a lender for accurate quotes based on your financial situation.
        </p>
      </div>
    </section>
  );
};

export default MortgageCalculator;
